import React, { useRef, useState, useCallback } from 'react';
import { Box, Typography, Button, Paper } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowUp } from '@fortawesome/pro-solid-svg-icons';
import { toast } from 'react-toastify';

interface DocumentUploadProps {
  onFilesSelected: (files: File[]) => void;
}

const ALLOWED_EXTENSIONS = ['.pdf', '.docx', '.xlsx', '.csv'];

const hasAllowedExtension = (filename: string): boolean =>
  ALLOWED_EXTENSIONS.some((extension) =>
    filename.toLowerCase().endsWith(extension),
  );

const DocumentUpload: React.FC<DocumentUploadProps> = ({ onFilesSelected }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const processFiles = (files: FileList | null) => {
    if (!files) return;

    const validFiles: File[] = [];
    const invalidFiles: string[] = [];

    Array.from(files).forEach((file) => {
      if (hasAllowedExtension(file.name)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    if (invalidFiles.length > 0) {
      toast.error(
        `Unsupported file type: ${invalidFiles.join(
          ', ',
        )}. Allowed formats: PDF, DOCX, XLSX, CSV.`,
      );
    }

    if (validFiles.length > 0) {
      onFilesSelected(validFiles);
    }
  };

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      processFiles(e.dataTransfer.files);
    },
    [onFilesSelected],
  );

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    processFiles(e.target.files);
    // Reset the input value so the same files can be selected again if needed
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        borderStyle: 'dashed',
        borderWidth: 2,
        borderColor: isDragging ? 'primary.main' : 'divider',
        backgroundColor: isDragging
          ? 'rgba(25, 118, 210, 0.04)'
          : 'background.paper',
        padding: 6,
        textAlign: 'center',
        borderRadius: 2,
        transition: 'all 0.2s ease-in-out',
        cursor: 'pointer',
        width: '100%',
        boxSizing: 'border-box',
      }}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleBrowseClick}
    >
      <input
        type="file"
        multiple
        ref={fileInputRef}
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
        accept=".pdf,.docx,.xlsx,.csv"
      />
      <Box
        sx={{
          backgroundColor: '#f3f4f6',
          width: 56,
          height: 56,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto',
          mb: 2,
        }}
      >
        <FontAwesomeIcon icon={faCloudArrowUp} size="lg" color="#9ca3af" />
      </Box>
      <Typography variant="h6" gutterBottom fontWeight="bold">
        Drag and drop files
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Supported formats: PDF, DOCX, XLSX, and CSV.
      </Typography>
      <Button
        variant="outlined"
        color="secondary"
        onClick={(e) => {
          e.stopPropagation();
          handleBrowseClick();
        }}
        sx={{
          textTransform: 'none',
          borderRadius: 2,
        }}
      >
        Browse local storage
      </Button>
    </Paper>
  );
};

export default DocumentUpload;
